import React, { useRef, forwardRef, useImperativeHandle } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import BreatheEV_Logo from "../../Images/logo.png";
import { Image } from "antd";
import styles from "./styles.module.css";

const BidPreview = forwardRef(({ bidToView }, ref) => {
  const printRef = useRef();
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const newBidData = bidToView;

  const getTableBodyData = () => {
  const tableBody = [];
  const rowMeta = [];
  const allItems = [];

  // 1. Helper to collect standard items
  const collectStandard = (data) => {
    if (data?.quantity) {
      allItems.push({
        ...data,
        // Ensure standard items have a default orderNumber if missing
        orderNumber: data.orderNumber ?? 0 
      });
    }
  };

  // 2. Push all potential objects into the collection array
  collectStandard(newBidData?.l2Charger);
  collectStandard(newBidData?.l3Charger);
  collectStandard(newBidData?.electricalWork);
  collectStandard(newBidData?.wifiConnectivity);
  collectStandard(newBidData?.wifiAntenna);
  collectStandard(newBidData?.warranty);
  collectStandard(newBidData?.signage);
  collectStandard(newBidData?.pedestals);
  collectStandard(newBidData?.simCard4G);
  collectStandard(newBidData?.siteWiFi);
  collectStandard(newBidData?.chargerInstallOnly);
  collectStandard(newBidData?.shipping);

  // 3. Normalize and push custom data
  if (newBidData?.bidCustomData?.length > 0) {
    newBidData.bidCustomData.forEach((item) => {
      if (item?.fieldQuantity) {
        allItems.push({
          title: item.fieldTitle || "Custom Item",
          description: item.fieldDescription || "",
          pricePerUnit: item.fieldPricePerUnit,
          quantity: item.fieldQuantity,
          margin: item.fieldMargin || 0,
          orderNumber: item.orderNumber ?? 0
        });
      }
    });
  }

  // 4. SORT based on orderNumber
  allItems.sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0));

  // 5. Build tableBody and rowMeta from sorted list
  allItems.forEach((data) => {
    const rateValue = ((data.pricePerUnit * data.quantity) + (data.margin || 0)) / data.quantity;
    const amountValue = (data.quantity * data.pricePerUnit) + (data.margin || 0);

    const rate = `$ ${rateValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    const amount = `$ ${amountValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

    tableBody.push([
      (data.title || "Item") + "\n" + (data.description || ""),
      rate,
      data.quantity.toString(),
      amount
    ]);

    rowMeta.push({ 
      title: data.title || "Item", 
      description: data.description || "" 
    });
  });

  return { tableBody, rowMeta };
};

  // const getTableBodyData = () => {
  //   const tableBody = [];
  //   const rowMeta = [];

  //   const addDataRow = (data) => {
  //     if (data?.quantity) {
  //       const rate = `$ ${(((data.pricePerUnit * data.quantity) + data.margin) / data.quantity).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  //       const amount = `$ ${((data.quantity * data.pricePerUnit) + data.margin).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  //       tableBody.push([
  //         (data.title || "Item") + "\n" + (data.description || ""),
  //         rate,
  //         data.quantity.toString(),
  //         amount
  //       ]);
  //       rowMeta.push({ title: data.title || "Item", description: data.description || "" });
  //     }
  //   };

  //   addDataRow(newBidData?.l2Charger);
  //   addDataRow(newBidData?.l3Charger);
  //   addDataRow(newBidData?.electricalWork);
  //   addDataRow(newBidData?.wifiConnectivity);
  //   addDataRow(newBidData?.wifiAntenna);
  //   addDataRow(newBidData?.warranty);
  //   addDataRow(newBidData?.signage);
  //   addDataRow(newBidData?.pedestals);
  //   addDataRow(newBidData?.simCard4G);
  //   addDataRow(newBidData?.siteWiFi);
  //   addDataRow(newBidData?.chargerInstallOnly);

  //   if (newBidData?.bidCustomData?.length > 0) {
  //     newBidData.bidCustomData.forEach((item) => {
  //       if (item?.fieldQuantity) {
  //         const rate = `$ ${(((item.fieldPricePerUnit * item.fieldQuantity) + item.fieldMargin) / item.fieldQuantity).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  //         const amount = `$ ${((item.fieldQuantity * item.fieldPricePerUnit) + item.fieldMargin).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  //         tableBody.push([
  //           (item.fieldTitle || "Custom Item") + "\n" + (item.fieldDescription || ""),
  //           rate,
  //           item.fieldQuantity.toString(),
  //           amount
  //         ]);
  //         rowMeta.push({ title: item.fieldTitle || "Custom Item", description: item.fieldDescription || "" });
  //       }
  //     });
  //   }

  //   addDataRow(newBidData?.shipping);
  //   return { tableBody, rowMeta };
  // };

  const createJsPdf = async (watermark = false) => {
    const element = printRef.current;
    if (!element) return Promise.reject("No element found");

    const pdf = new jsPDF("p", "pt", "letter");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const marginPt = 10 * 2.83465; // ~28.34 pt
    const renderWidth = pdfWidth - (marginPt * 2);

    const headerEl = headerRef.current;
    const headerCanvas = await html2canvas(headerEl, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
    const headerImg = headerCanvas.toDataURL("image/png", 1.0);
    const headerRenderHeight = (headerCanvas.height * renderWidth) / headerCanvas.width;

    pdf.addImage(headerImg, "PNG", marginPt, marginPt, renderWidth, headerRenderHeight);

    let finalY = marginPt + headerRenderHeight + 20;

    const { tableBody, rowMeta } = getTableBodyData();

    autoTable(pdf, {
      startY: finalY,
      head: [['ITEMS & DESCRIPTION', 'RATE', 'QTY', 'AMOUNT']],
      body: tableBody,
      margin: { left: marginPt, right: marginPt },
      theme: 'grid',
      headStyles: { fillColor: [233, 234, 235], textColor: 0, fontSize: 10, fontStyle: 'bold' },
      bodyStyles: { textColor: 0, fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { halign: 'right', cellWidth: 80, fontStyle: 'bold' },
        2: { halign: 'right', cellWidth: 40, fontStyle: 'bold' },
        3: { halign: 'right', cellWidth: 80, fontStyle: 'bold' }
      },
      styles: { lineColor: [153, 153, 153], lineWidth: 0.5, cellPadding: 6, font: 'helvetica', overflow: 'linebreak' },
      rowPageBreak: 'avoid',
      willDrawCell: (data) => {
        // Clear default text for column 0 body cells so we can draw bold title manually
        if (data.section === 'body' && data.column.index === 0) {
          data.cell.text = [];
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 0) {
          const meta = rowMeta[data.row.index];
          if (!meta) return;

          const pad = 6; // matches cellPadding
          const fontSize = 10;
          const lineHeight = fontSize * 1.15;
          const textWidth = data.cell.width - pad * 2;
          const x = data.cell.x + pad;
          let y = data.cell.y + pad + fontSize * 0.75;

          pdf.setFontSize(fontSize);
          pdf.setTextColor(0, 0, 0);

          // Draw title in bold
          pdf.setFont('helvetica', 'bold');
          const titleLines = pdf.splitTextToSize(meta.title, textWidth);
          pdf.text(titleLines, x, y);
          y += titleLines.length * lineHeight;

          // Draw description in normal weight
          if (meta.description) {
            pdf.setFont('helvetica', 'normal');
            const descLines = pdf.splitTextToSize(meta.description, textWidth);
            pdf.text(descLines, x, y);
          }
        }
      }
    });

    finalY = pdf.lastAutoTable.finalY + 20;

    const footerEl = footerRef.current;
    const footerCanvas = await html2canvas(footerEl, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
    const footerImg = footerCanvas.toDataURL("image/png", 1.0);
    const footerRenderHeight = (footerCanvas.height * renderWidth) / footerCanvas.width;

    if (finalY + footerRenderHeight > pdfHeight - marginPt) {
      pdf.addPage();
      finalY = marginPt;
    }

    pdf.addImage(footerImg, "PNG", marginPt, finalY, renderWidth, footerRenderHeight);

    if (watermark) {
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setGState(new pdf.GState({ opacity: 0.15 }));
        pdf.setTextColor(90, 0, 0);
        pdf.setFontSize(120);
        pdf.text("SAMPLE", pdfWidth / 2, pdfHeight / 2, { angle: 45, align: "center", baseline: "middle" });
        pdf.setGState(new pdf.GState({ opacity: 1 }));
      }
    }

    return pdf;
  };

  useImperativeHandle(ref, () => ({
    generateBlob: async ({ watermark = false } = {}) => {
      const pdf = await createJsPdf(watermark);
      return pdf.output("blob");
    }
  }));

  const downloadPDF = async () => {
    const pdf = await createJsPdf(false);
    pdf.save(`Proposal-${bidToView.bidNumber}.pdf`);
  };

  const getEmailFontSize = (email = '') => {
    const len = email.length;
    if (len >= 44) return '10px';
    if (len >= 40) return '11px';
    if (len > 38) return '12px';
    if (len > 32) return '12px';
    if (len > 26) return '13px';
    if (len > 20) return '14px';
    return '14px';
  };

  const rows = [];

  const addRow = (data) => {
    if (data?.quantity) {
      rows.push(
        <tr key={data.title}>
          <td>
            <span className={styles['table-item-title']}>
              <b>{data.title}</b>
            </span>
            <br></br>
            <span className={styles['table-item-description']}>
              {data.description}
            </span>
          </td>
          <td className={styles['table-items-right']}><b>$ {(((data.pricePerUnit * data.quantity) + data.margin) / data.quantity).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</b></td>
          <td className={styles['table-items-right']}><b>{data.quantity}</b></td>
          <td className={styles['table-items-right']}><b>$ {((data.quantity * data.pricePerUnit) + data.margin).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</b></td>
        </tr>
      );
    }
  };

  const lineItems = [
    newBidData?.l2Charger,
    newBidData?.l3Charger,
    newBidData?.electricalWork,
    newBidData?.wifiConnectivity,
    newBidData?.wifiAntenna,
    newBidData?.warranty,
    newBidData?.signage,
    newBidData?.pedestals,
    newBidData?.simCard4G,
    newBidData?.siteWiFi,
    newBidData?.chargerInstallOnly,
    newBidData?.shipping
  ].filter(item => item?.quantity);
  
  
  
  if (newBidData?.bidCustomData?.length > 0) {
    newBidData.bidCustomData.forEach((item) => {
      if (item?.fieldQuantity) {
        lineItems.push({
          title: item.fieldTitle || "Custom Item",
          description: item.fieldDescription,
          pricePerUnit: item.fieldPricePerUnit,
          quantity: item.fieldQuantity,
          margin: item.fieldMargin,
          orderNumber: item.orderNumber || 0 // Ensure custom fields have an orderNumber
        });
      }
    });
  }
  
  lineItems.sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0));
  
  lineItems.forEach((data, index) => {
    rows.push(
      <tr key={data.title + index}>
        <td>
          <b>{data.title}</b> <br />
          {data.description}
        </td>
        <td className={styles['table-items-right']}>
          $ {(((data.pricePerUnit * data.quantity) + data.margin) / data.quantity)
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
        </td>
        <td className={styles['table-items-right']}>{data.quantity}</td>
        <td className={styles['table-items-right']}>
          $ {((data.quantity * data.pricePerUnit) + data.margin)
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
        </td>
      </tr>
    );
  });
  

  // addRow(newBidData?.l2Charger);
  // addRow(newBidData?.l3Charger);
  // addRow(newBidData?.electricalWork);
  // addRow(newBidData?.wifiConnectivity);
  // addRow(newBidData?.wifiAntenna);
  // addRow(newBidData?.warranty);
  // addRow(newBidData?.signage);
  // addRow(newBidData?.pedestals);
  // addRow(newBidData?.simCard4G);
  // addRow(newBidData?.siteWiFi);
  // addRow(newBidData?.chargerInstallOnly);

  // if (newBidData?.bidCustomData?.length > 0) {
  //   newBidData.bidCustomData.forEach((item, index) => {
  //     if (item?.fieldQuantity) {
  //       rows.push(
  //         <tr key={`custom-${index}`}>
  //           <td>
  //             <span className={styles['table-item-title']}>
  //               <b>{item.fieldTitle || "Custom Item"}</b>
  //             </span>
  //             <br></br>
  //             <span className={styles['table-item-description']}>
  //               {item.fieldDescription}
  //             </span>
  //           </td>
  //           <td className={styles['table-items-right']}><b>$ {(((item.fieldPricePerUnit * item.fieldQuantity) + item.fieldMargin) / item.fieldQuantity).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</b></td>
  //           <td className={styles['table-items-right']}><b>{item.fieldQuantity}</b></td>
  //           <td className={styles['table-items-right']}><b>$ {((item.fieldQuantity * item.fieldPricePerUnit) + item.fieldMargin).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</b></td>
  //         </tr>
  //       );
  //     }
  //   });
  // }

  // addRow(newBidData?.shipping);

  function formatUSPhoneNumber(phoneNumberString) {
    var cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return null;
  }

  return (
    <>
      <div
        ref={printRef}
        style={{
          padding: "20px",
          backgroundColor: "white",
          color: "black",
          width: "794px",
          fontFamily: "Montserrat, sans-serif",
        }}
      >
        <div ref={headerRef} style={{ backgroundColor: 'white' }}>
          <div className={styles['top-margin']}></div>
          <div className={styles['pdf-header']}>
            <div className={styles['pdf-header-logo']}>
              <Image className={styles['pdf-image']} src={BreatheEV_Logo} alt="BreatheEV Logo" />
              <div className={styles['pdf-header-proposal-number']}>
                <b>PROPOSAL</b><br />
                {bidToView.bidNumber}
              </div>
            </div>
            <div className={styles["pdf-contact"]}>
              <h2>BreatheEV</h2>
              {bidToView.name} <br></br>
              {bidToView.email} <br></br>
              {formatUSPhoneNumber(bidToView.phone)}
              <br></br>
              <br></br>
              <p>
                <b>Date</b> <br></br>
                {new Date(bidToView.dateCreated).toLocaleDateString()}
              </p>
            </div>

            <div className={styles['pdf-header-prepared-for']}>
              <p>PREPARED FOR</p>
              {
                <>
                  {bidToView.dealId?.dealname ? bidToView.dealId?.dealname : <></>} <br></br>
                  {bidToView.dealId?.property_address ? bidToView.dealId?.property_address : <></>} <br></br> <br></br>
                  {bidToView.dealPOC?.name ? bidToView.dealPOC?.name : <></>} <br></br>
                  {bidToView.dealPOC?.email
                    ? <span className={styles['pdf-poc-email']} style={{ fontSize: getEmailFontSize(bidToView.dealPOC.email) }}>{bidToView.dealPOC.email}</span>
                    : <></>}
                  {bidToView.dealPOC?.phone ? bidToView.dealPOC?.phone : <></>}
                </>
              }
            </div>
          </div>
        </div>

        <table
          border="1"
          cellPadding="6"
          className={styles["table"]}
        >
          <thead>
            <tr>
              <th>ITEMS & DESCRIPTION</th>
              <th>RATE</th>
              <th>QTY</th>
              <th>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows
            ) : (
              <tr>
                <td colSpan="4">No items available</td>
              </tr>
            )}
          </tbody>
        </table>

        <div ref={footerRef} style={{ backgroundColor: 'white' }}>
          <div className={styles['pdf-footer-container']}>
            <div className={styles['pdf-footer-container-left']}>
              <p className={styles['terms-and-conditions']}>
                <b>Terms and Conditions:</b> <br></br>
                {bidToView.termsAndConditions}
              </p>
            </div>
            <div className={styles['pdf-footer-container-right']}>
              <p>SUBTOTAL:
                <span className={styles['pdf-footer-values']}>
                  <b>
                    $ {bidToView.subTotal ? bidToView.subTotal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "0.00"}
                  </b>
                </span>
              </p>
              <p>{`SALES TAX (${bidToView.salesTaxPercent ? `${!Number.isInteger(bidToView.salesTaxPercent) ? Number(bidToView.salesTaxPercent).toFixed(2) : bidToView.salesTaxPercent}%` : '$'}):`}
                <span className={styles['pdf-footer-values']}>
                  <b>
                    <b>$ {bidToView.salesTax ? bidToView.salesTax.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "0.00"}</b>
                  </b>
                </span>
              </p>
              {bidToView.rebateTitle && <p>
                {bidToView.rebateTitle}:
                <span className={styles['pdf-footer-values']}>
                  <b>
                    <b>-$ {bidToView.rebateAmount ? bidToView.rebateAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "0.00"}</b>
                  </b>
                </span>
              </p>}

              <hr></hr>
              <p className={styles['pdf-total-cost']}>
                <b>Total </b>
                <span className={styles['pdf-footer-values']}>
                  <b>
                    <b>$ {bidToView.totalCost ? bidToView.totalCost.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "0.00"}</b>
                  </b>
                </span>
              </p>

              <p className={styles['terms-and-conditions']}>
                {bidToView.customDescription}
              </p>
            </div>
          </div>
        </div>
      </div>

      <button
        disabled={bidToView.bidStatus !== "APPROVED"}
        onClick={downloadPDF}
        style={{
          marginTop: "20px",
          padding: "10px 15px",
          background: bidToView.bidStatus === "APPROVED" ? "#FF4040" : "#ccc",
          color: "white",
          border: "none",
          borderRadius: "8px",
          cursor: bidToView.bidStatus === "APPROVED" ? "pointer" : "not-allowed",
        }}
      >
        Download PDF
      </button>
    </>
  );
});

export default BidPreview;
